import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import {
  Carte,
  Chargement,
  DateHeure,
  Erreur,
  Etiquette,
  Nombre,
  Vide,
  formaterDateHeure,
} from '../../composants/communs.tsx';
import { BoutonImprimer, exporterTableauPdf } from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { FormulaireEnvoi } from './FormulaireEnvoi.tsx';
import { ReceptionTransfert } from './ReceptionTransfert.tsx';
import type { LigneTransfert } from './types.ts';

/**
 * Transferts entre boutiques — RG-050 à RG-054.
 *
 * Deux questions, dans cet ordre : qu'est-ce qui m'arrive (à recevoir) ?
 * qu'ai-je envoyé ou reçu (historique) ? Les arrivées passent en tête : une
 * marchandise en route n'est vendable nulle part tant qu'on ne l'a pas reçue.
 */
export function Transferts(): ReactNode {
  const { boutiqueId, peut } = useSession();
  const [envoi, setEnvoi] = useState(false);
  const [ouvert, setOuvert] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['transferts', boutiqueId],
    queryFn: () =>
      appeler<{ donnees: LigneTransfert[] }>(
        `/transferts${boutiqueId === null ? '' : `?boutique_id=${boutiqueId}`}`,
      ),
    select: (reponse) => reponse.donnees,
  });
  const arrivees = (data ?? []).filter((transfert) => transfert.a_recevoir);

  return (
    <>
      <Entete
        titre="Transferts"
        sous="La marchandise quitte la source dès l’envoi, et n’entre à destination qu’à la réception."
      />
      <Erreur erreur={error} />

      <div className="pile">
        {peut('transfert.expedier') &&
          (envoi ? (
            <FormulaireEnvoi
              onFerme={() => {
                setEnvoi(false);
              }}
            />
          ) : (
            <div>
              <button
                type="button"
                onClick={() => {
                  setEnvoi(true);
                }}
              >
                Nouvel envoi
              </button>
            </div>
          ))}

        {ouvert !== null && (
          <ReceptionTransfert
            transfertId={ouvert}
            onFerme={() => {
              setOuvert(null);
            }}
          />
        )}

        {arrivees.length > 0 && (
          <Carte titre={`À recevoir (${String(arrivees.length)})`}>
            <Liste transferts={arrivees} onOuvrir={setOuvert} libelle="Réceptionner" />
          </Carte>
        )}

        <Carte titre="Historique">
          {isPending ? (
            <Chargement />
          ) : data === undefined || data.length === 0 ? (
            <Vide texte="Aucun transfert." />
          ) : (
            <Liste transferts={data} onOuvrir={setOuvert} libelle="Voir" />
          )}
        </Carte>
      </div>
    </>
  );
}

function Liste({
  transferts,
  onOuvrir,
  libelle,
}: {
  transferts: LigneTransfert[];
  onOuvrir: (id: string) => void;
  libelle: string;
}): ReactNode {
  const { ouvrir } = useApercuPdf();
  return (
    <>
      <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
        <BoutonImprimer
          onClick={() => {
            ouvrir(() =>
              exporterTableauPdf({
                titre: 'Transferts',
              nomFichier: 'transferts',
              colonnes: [
                { cle: 'numero', entete: 'Numéro', rendu: () => null, texte: (t: LigneTransfert) => t.numero },
                {
                  cle: 'trajet',
                  entete: 'Trajet',
                  rendu: () => null,
                  texte: (t: LigneTransfert) => `${t.source} -> ${t.destination}`,
                },
                {
                  cle: 'envoye_le',
                  entete: 'Envoyé le',
                  rendu: () => null,
                  texte: (t: LigneTransfert) => formaterDateHeure(t.expedie_le),
                },
                { cle: 'par', entete: 'Par', rendu: () => null, texte: (t: LigneTransfert) => t.expedie_par },
                {
                  cle: 'quantite',
                  entete: 'Quantité',
                  numerique: true,
                  rendu: () => null,
                  texte: (t: LigneTransfert) =>
                    t.quantite_recue !== null && t.quantite_recue !== t.quantite_expediee
                      ? `${String(t.quantite_expediee)} (reçu ${String(t.quantite_recue)})`
                      : String(t.quantite_expediee),
                },
                {
                  cle: 'statut',
                  entete: 'Statut',
                  rendu: () => null,
                  texte: (t: LigneTransfert) =>
                    t.statut === 'EXPEDIE'
                      ? 'En route'
                      : t.quantite_recue !== null && t.quantite_recue !== t.quantite_expediee
                        ? 'Reçu avec écart'
                        : 'Reçu',
                },
              ],
                lignes: transferts,
              }),
            );
          }}
        />
      </div>
      <div className="tableau-defilant">
      <table>
        <thead>
          <tr>
            <th>Numéro</th>
            <th>Trajet</th>
            <th>Envoyé le</th>
            <th>Par</th>
            <th>Quantité</th>
            <th>Statut</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {transferts.map((transfert) => (
            <tr key={transfert.id}>
              <td className="mono">{transfert.numero}</td>
              <td className="mono">
                {transfert.source} → {transfert.destination}
              </td>
              <td>
                <DateHeure valeur={transfert.expedie_le} />
              </td>
              <td>{transfert.expedie_par}</td>
              <td>
                <Nombre valeur={transfert.quantite_expediee} />
                {transfert.quantite_recue !== null &&
                  transfert.quantite_recue !== transfert.quantite_expediee && (
                    <> (reçu <Nombre valeur={transfert.quantite_recue} />)</>
                  )}
              </td>
              <td>
                {transfert.statut === 'EXPEDIE' ? (
                  <Etiquette ton="ambre" texte="en route" />
                ) : transfert.quantite_recue !== null &&
                  transfert.quantite_recue !== transfert.quantite_expediee ? (
                  <Etiquette ton="rouge" texte="reçu avec écart" />
                ) : (
                  <Etiquette ton="vert" texte="reçu" />
                )}
              </td>
              <td>
                <button
                  type="button"
                  className="bouton--discret"
                  onClick={() => {
                    onOuvrir(transfert.id);
                  }}
                >
                  {transfert.a_recevoir ? 'Réceptionner' : libelle}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}
